import React from "react";
import CaseStudy from "../../components/caseStudy/CaseStudy";
import CRMBuzzDemo from "../../assets/crmbuzz.mp4";
import { FaReact, FaNodeJs, FaGitAlt } from "react-icons/fa";
import { SiExpress, SiPostgresql, SiJsonwebtokens, SiGooglegemini } from "react-icons/si";
import { MdAutoAwesome } from "react-icons/md";

// Content only — the layout lives in components/caseStudy/CaseStudy.jsx.
const study = {
  slug: "crmbuzz",
  title: "CRMBuzz",
  subtitle:
    "A full-stack, API-driven CRM/CMS platform where the backend controls the structure and content of dynamic business websites — administrators manage pages, navigation, sections, theme and media without ever touching frontend code, with an AI copilot that assists rather than replaces that workflow.",
  badges: [
    { label: "White-Label CRM & Dynamic Website Builder" },
    { label: "AI Website Copilot", icon: <MdAutoAwesome size={13} /> },
  ],
  tags: ["React", "Node.js", "Express", "PostgreSQL", "JWT / 2FA", "Google Gemini AI"],
  facts: [
    { label: "Type", value: "White-label CRM, CMS and public website in one platform" },
    { label: "AI", value: "Google Gemini copilot — Generate → Preview → Accept" },
    { label: "Admin languages", value: "French, English, German" },
    { label: "Source", value: "Available on request" },
  ],
  media: [{ type: "video", src: CRMBuzzDemo, label: "Product demo" }],

  overview: {
    title: "What is CRMBuzz?",
    paragraphs: [
      "CRMBuzz combines a CRM-style admin dashboard, a content management system, and a dynamic public website into one API-driven application. Administrators log into a dashboard to control every part of a business's website — its pages, navigation, content sections, images and visual theme — and those changes are reflected immediately on the live public site. The frontend holds no hardcoded business content anywhere; every page is assembled at render time from data the backend provides.",
      "An AI copilot sits inside that same admin dashboard — rewriting, translating and auditing content on request — but it never bypasses the CRUD architecture underneath it: every suggestion is generated, previewed and explicitly accepted before it's saved through the same APIs a manual edit would use.",
    ],
  },
  problem: {
    paragraphs: [
      "Traditional business websites tightly couple content to code: changing a headline, swapping an image, adding a page, or reordering a section usually means a developer editing components directly. That works for a one-off brochure site, but it doesn't scale to a platform meant to serve many businesses — and it puts routine content changes behind a development bottleneck they shouldn't need.",
      "CRMBuzz removes that coupling by moving configurable content into the backend and database, exposed through REST APIs. The frontend becomes a rendering layer, not a content owner.",
    ],
  },
  solution: {
    steps: [
      "Admin Dashboard",
      "REST APIs",
      "Backend / Business Logic (Node.js + Express)",
      "PostgreSQL",
      "Dynamic Public Website (React)",
    ],
    note:
      "The database is the single source of truth for structure and content. The React frontend never owns business content — it receives page and section data over the API and renders it through a generic, type-driven renderer.",
  },

  features: [
    {
      title: "Dynamic Website Management",
      description: "Administrators manage all website content without touching frontend code.",
    },
    {
      title: "Dynamic Pages & Navigation",
      description: "Pages and navbar links are configurable and stay synchronized through stable page identifiers, not editable labels.",
    },
    {
      title: "Section-Based Page Builder",
      description: "Pages are composed of reusable, typed content sections that can be added, edited, removed and reordered.",
    },
    {
      title: "Drag & Drop Ordering",
      description: "Administrators visually reorder a page's sections directly in the builder.",
    },
    {
      title: "Live Theme Management",
      description: "One admin-picked color generates a full brand palette, applied site-wide instantly via CSS custom properties.",
    },
    {
      title: "Rich Text Editing",
      description: "Long-form content is authored through a full rich-text editor, sanitized before it ever reaches the public site.",
    },
    {
      title: "Media Library",
      description: "Centralized image upload and reuse across every image field in the admin.",
    },
    {
      title: "AI Content Assistant",
      description: "Any text field can be rewritten, shortened, translated or made more persuasive by AI — always Generate → Preview → Accept, never a silent overwrite.",
    },
    {
      title: "AI SEO Generator",
      description: "Generates a meta title and description from a page's actual content, reviewed before it's saved.",
    },
    {
      title: "AI Website Audit",
      description: "A dashboard widget scores content completeness, SEO readiness, CTA coverage and consistency from real site data — the AI only phrases the recommendations, never the underlying numbers.",
    },
    {
      title: "Multi-Language Admin (FR/EN/DE)",
      description: "The entire admin interface is translated and switchable live, independent of the public site's own content.",
    },
    {
      title: "Auth & Security",
      description: "JWT sessions plus TOTP two-factor authentication; every admin API route is protected by middleware.",
    },
    {
      title: "White-Label Architecture",
      description: "The same platform serves different business types without any frontend rebuild.",
    },
  ],

  diagrams: [
    { label: "Request flow", steps: ["React Frontend", "REST APIs", "Node.js / Express Backend", "PostgreSQL"] },
    {
      label: "Content flow",
      steps: [
        "Admin",
        "Configuration (pages, sections, nav, theme, media)",
        "Backend (routes + validation)",
        "PostgreSQL",
        "Public Website — dynamic rendering",
      ],
    },
  ],

  challenges: [
    {
      title: "Avoiding hardcoded website content",
      problem: "Business content embedded inside React components makes a site impossible to reuse or hand off.",
      solution: "Every content component is purely presentational — it receives configuration as props and renders it. Missing content renders a clearly-marked neutral placeholder instead of invented business copy.",
    },
    {
      title: "Synchronizing navigation and pages",
      problem: "If navigation links reference a page by its editable title, renaming that page silently breaks the site's navigation.",
      solution: "Navigation always resolves through a page's stable ID/slug, never its display label, so labels can be freely edited without breaking a link.",
    },
    {
      title: "Reusable page architecture",
      problem: "Building a separate admin form and renderer for every page type doesn't scale.",
      solution: "A single generic section/block system — each block type is one config schema plus one renderer, and any page is just an ordered list of blocks.",
    },
    {
      title: "The empty-database case",
      problem: "A CMS that only looks good when full of content is broken by definition — it has to survive a fresh install or a full content wipe.",
      solution: "A default-structure/bootstrap system creates real, empty content rows, and every component renders a distinct placeholder state instead of hardcoded fallback text.",
    },
    {
      title: "Reusability across businesses",
      problem: "A platform coupled to one business's assumptions can't be reused for another.",
      solution: "Pages, sections, themes and navigation are all configuration, not code — validated by reconfiguring the same codebase into a different business end-to-end through the admin panel.",
    },
    {
      title: "Integrating AI without compromising the architecture",
      problem: "Bolting AI onto an existing CRUD system risks turning it into an unpredictable black box that can corrupt data, bypass validation, or lock the app into one vendor.",
      solution: "The AI layer only ever returns a suggestion — it never writes to the database. Every response is validated against a strict schema before reaching the UI, and accepting a suggestion routes through the exact same CRUD APIs a manual edit would use. The provider itself sits behind a single interface, so the underlying model (currently Google Gemini) can be swapped without touching any calling code.",
    },
  ],

  stack: [
    { group: "Frontend", items: ["React 18", "React Router", "JavaScript (JSX)", "Custom i18n (FR/EN/DE)"] },
    { group: "Backend", items: ["Node.js", "Express", "REST APIs"] },
    { group: "Database", items: ["PostgreSQL"] },
    { group: "Security", items: ["JWT sessions", "TOTP 2FA", "bcrypt"] },
    { group: "Content / Media", items: ["TipTap rich text", "DOMPurify", "Multer uploads"] },
    { group: "AI", items: ["Google Gemini API", "Provider-agnostic AI abstraction", "Schema-validated structured output"] },
    { group: "Tools", items: ["Git"] },
  ],
  stackIcons: [
    [FaReact, "React"],
    [FaNodeJs, "Node.js"],
    [SiExpress, "Express"],
    [SiPostgresql, "PostgreSQL"],
    [SiJsonwebtokens, "JWT"],
    [SiGooglegemini, "Google Gemini"],
    [FaGitAlt, "Git"],
  ],
};

const CRMBuzzCaseStudy = () => <CaseStudy study={study} />;

export default CRMBuzzCaseStudy;
